import type { AuthUser, UserRole } from './auth';

export const PARTNERS_READ_CAPABILITY = 'partners.read';
export const PARTNERS_UPDATE_CAPABILITY = 'partners.update';
export const PARTNERS_ASSOCIATION_OVERRIDE_CAPABILITY = 'partners.associations.override';
export const REVENUE_PLANNING_READ_CAPABILITY = 'revenue-planning.read';
export const REVENUE_PLANNING_WRITE_CAPABILITY = 'revenue-planning.write';
export const REVENUE_PLANNING_DELETE_CAPABILITY = 'revenue-planning.delete';
export const ORDERS_READ_CAPABILITY = 'orders.read';
export const ORDERS_EDIT_CAPABILITY = 'orders.edit';
export const ORDERS_UPDATE_CAPABILITY = 'orders.update';
export const ORDERS_TRANSITION_CAPABILITY = 'orders.status.transition';
export const ORDERS_REFUND_CAPABILITY = 'orders.refund';
export const ORDERS_SENSITIVE_READ_CAPABILITY = 'orders.sensitive.read';
export const ORDERS_ASSETS_READ_CAPABILITY = 'orders.assets.read';
export const ORDERS_NOTES_READ_CAPABILITY = 'orders.notes.read';
export const ORDERS_NOTES_CREATE_CAPABILITY = 'orders.notes.create';
export const ANALYTICS_TASKS_READ_CAPABILITY = 'analytics.tasks.read';
export const ANALYTICS_TASKS_CREATE_CAPABILITY = 'analytics.tasks.create';
export const ANALYTICS_TASKS_UPDATE_CAPABILITY = 'analytics.tasks.update';
export const ANALYTICS_TASKS_ASSIGN_CAPABILITY = 'analytics.tasks.assign';
export const AGENTS_READ_CAPABILITY = 'agents.read';
export const AGENTS_AUTHOR_CAPABILITY = 'agents.author';
export const AGENTS_REVIEW_CAPABILITY = 'agents.review';
export const AGENTS_PUBLISH_CAPABILITY = 'agents.publish';
export const AGENTS_AUDIT_CAPABILITY = 'agents.audit';
export const AGENTS_DATASETS_REVIEW_CAPABILITY = 'agents.datasets.review';
export const AGENTS_PRIVACY_CAPABILITY = 'agents.privacy';
export const AGENTS_OPERATIONS_CAPABILITY = 'agents.operations';

export function getEffectiveRoles(user: AuthUser | null): UserRole[] {
  if (!user) return [];
  if (user.roles && user.roles.length > 0) return user.roles;
  return [user.role];
}

export function hasAnyRole(user: AuthUser | null, roles: UserRole[]): boolean {
  const effectiveRoles = getEffectiveRoles(user);
  return effectiveRoles.some((role) => roles.includes(role));
}

export function hasCapability(user: AuthUser | null, capability: string): boolean {
  if (!user?.capabilities) return false;
  return user.capabilities.includes(capability);
}

export function hasAllCapabilities(user: AuthUser | null, capabilities: readonly string[]): boolean {
  if (!user || capabilities.length === 0) return false;
  return capabilities.every((capability) => hasCapability(user, capability));
}

export function canAccessPartners(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3, 4]) || hasCapability(user, PARTNERS_READ_CAPABILITY);
}

export function canUpdatePartners(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3, 4]) || hasCapability(user, PARTNERS_UPDATE_CAPABILITY);
}

export function canOverridePartnerAssociation(user: AuthUser | null): boolean {
  return hasAnyRole(user, [4]) || hasCapability(user, PARTNERS_ASSOCIATION_OVERRIDE_CAPABILITY);
}

export function canReadRevenuePlanning(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3]) || hasCapability(user, REVENUE_PLANNING_READ_CAPABILITY);
}

export function canWriteRevenuePlanning(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3]) || hasCapability(user, REVENUE_PLANNING_WRITE_CAPABILITY);
}

export function canDeleteRevenuePlanning(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3]) || hasCapability(user, REVENUE_PLANNING_DELETE_CAPABILITY);
}

export function canAccessCatalog(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3, 5]); // Admin=3 or CatalogMng=5
}

export function canAccessOrders(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3, 6]) || hasCapability(user, ORDERS_READ_CAPABILITY);
}

export function canEditOrders(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3, 6]) || hasCapability(user, ORDERS_EDIT_CAPABILITY);
}

export function canUpdateOrders(user: AuthUser | null): boolean {
  return hasCapability(user, ORDERS_UPDATE_CAPABILITY);
}

export function canTransitionOrders(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3, 6]) || hasCapability(user, ORDERS_TRANSITION_CAPABILITY);
}

export function canRefundOrders(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3]) && hasCapability(user, ORDERS_REFUND_CAPABILITY);
}

export function canReadSensitiveOrders(user: AuthUser | null): boolean {
  return hasAnyRole(user, [3]) || hasCapability(user, ORDERS_SENSITIVE_READ_CAPABILITY);
}

export function canReadOrderAssets(user: AuthUser | null): boolean {
  return hasAllCapabilities(user, [ORDERS_READ_CAPABILITY, ORDERS_ASSETS_READ_CAPABILITY]);
}

export function canReadOrderNotes(user: AuthUser | null): boolean {
  return hasAllCapabilities(user, [ORDERS_READ_CAPABILITY, ORDERS_NOTES_READ_CAPABILITY]);
}

export function canCreateOrderNotes(user: AuthUser | null): boolean {
  return hasAllCapabilities(user, [ORDERS_READ_CAPABILITY, ORDERS_NOTES_CREATE_CAPABILITY]);
}

export function canReadOrderTasks(user: AuthUser | null): boolean {
  return hasAllCapabilities(user, [ORDERS_READ_CAPABILITY, ANALYTICS_TASKS_READ_CAPABILITY]);
}

export function canCreateOrderTasks(user: AuthUser | null): boolean {
  return hasAllCapabilities(user, [
    ORDERS_READ_CAPABILITY,
    ANALYTICS_TASKS_CREATE_CAPABILITY,
    ANALYTICS_TASKS_ASSIGN_CAPABILITY,
  ]);
}

export function canUpdateOrderTasks(user: AuthUser | null): boolean {
  return hasAllCapabilities(user, [ORDERS_READ_CAPABILITY, ANALYTICS_TASKS_UPDATE_CAPABILITY]);
}

export function canAssignOrderTasks(user: AuthUser | null): boolean {
  return hasAllCapabilities(user, [
    ORDERS_READ_CAPABILITY,
    ANALYTICS_TASKS_UPDATE_CAPABILITY,
    ANALYTICS_TASKS_ASSIGN_CAPABILITY,
  ]);
}

export function canAccessAgents(user: AuthUser | null): boolean {
  return hasCapability(user, AGENTS_READ_CAPABILITY);
}
